<script setup>

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

// Custom field ID, used to get the department value
const departmentMetadataId = 15941428002

const greenhouseEndpoint = 'https://boards-api.greenhouse.io/v1/boards/sendbird/jobs/?content=true'

const jobs = ref([])
const locations = ref([])
const departments = ref([])
const filters = ref({ location: undefined, department: undefined })
const openDropdown = ref(null)

const uniq = (values) => [...new Set(values.filter(Boolean))]

const getJobLocation = (job) => job?.location?.name

const getJobDepartment = (job) => {
  const departmentIdx = job?.metadata
    ? job.metadata.findIndex((meta) => meta && meta.id === departmentMetadataId)
    : -1
  const meta = job?.metadata?.[departmentIdx]
  return meta?.value?.[0]
}

const populateJobs = (nextJobs) => {
  const nextLocations = []
  const nextDepartments = []

  nextJobs.forEach((job) => {
    nextLocations.push(getJobLocation(job))
    nextDepartments.push(getJobDepartment(job))
  })

  locations.value = uniq(nextLocations)
  departments.value = uniq(nextDepartments)
  jobs.value = nextJobs
}

const matchLocation = (job) =>
  !filters.value.location || getJobLocation(job) === filters.value.location

const matchDepartment = (job) =>
  !filters.value.department || getJobDepartment(job) === filters.value.department

const matchJob = (job) => matchDepartment(job) && matchLocation(job)

const filteredJobs = computed(() => jobs.value.filter(matchJob))
const positionsCount = computed(() => filteredJobs.value.length)

const groupedJobs = computed(() => {
  return departments.value
    .map((department) => ({
      department,
      jobs: jobs.value.filter((job) => matchJob(job) && getJobDepartment(job) === department)
    }))
    .filter((group) => group.jobs.length)
})

const jobMeta = (job) => [getJobDepartment(job), getJobLocation(job)].filter(Boolean).join(' • ')

const jobUrl = (job) => `/job/${job.id}`

const toggleDropdown = (name) => {
  openDropdown.value = openDropdown.value === name ? null : name
}

const selectLocation = (value) => {
  filters.value.location = value
  openDropdown.value = null
}

const selectDepartment = (value) => {
  filters.value.department = value
  openDropdown.value = null
}

const locationFilterRef = ref(null)
const departmentFilterRef = ref(null)

const closeDropdown = () => {
  openDropdown.value = null
}

const getActiveFilterEl = () => {
  if (openDropdown.value === 'location') return locationFilterRef.value
  if (openDropdown.value === 'department') return departmentFilterRef.value
  return null
}

const handleClickOutside = (event) => {
  const activeEl = getActiveFilterEl()
  if (!activeEl) return
  if (!activeEl.contains(event.target)) {
    closeDropdown()
  }
}

const handleKeydown = (event) => {
  if (event.key === 'Escape' && openDropdown.value) {
    closeDropdown()
  }
}

const fetchJobs = async () => {
  try {
    const response = await fetch(greenhouseEndpoint)
    const data = await response.json()
    populateJobs(data.jobs || [])
  } catch (error) {
    console.error('Failed to fetch Greenhouse jobs', error)
    populateJobs([])
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
  fetchJobs()
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="open-positions section-mb" id="jobs">
  <div class="container">

    <div class="open-positions__inner">
      <h2 v-if="props.block?.title" class="open-positions__title">
        {{ props.block.title }}
        <sup class="open-positions__count">[{{ positionsCount }}]</sup>
      </h2>
      <p v-if="props.block?.textBlock" class="open-positions__description">
        {{ props.block.textBlock }}
      </p>
    

    <!-- The dropdown filters -->
    <div class="open-positions__filters">

      <!-- Location filter -->
      <div
        ref="locationFilterRef"
        class="op-filter"
        :class="{ 'is-open': openDropdown === 'location' }"
      >
        <span class="op-filter__label">Location</span>
        <button type="button"
          class="op-filter__control"
          :class="{ 'is-placeholder': !filters.location }"
          :aria-expanded="openDropdown === 'location'"
          @click="toggleDropdown('location')">
          <span>{{ filters.location || 'Office location' }}</span>
          <span class="op-filter__chevron" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M5.83 8l4.17 4.17L14.17 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
        <ul v-show="openDropdown === 'location'" class="op-filter__list">
          <li>
            <button type="button" class="op-filter__option" @click="selectLocation(null)">Office location</button>
          </li>
          <li v-for="loc in locations" :key="loc">
            <button type="button" class="op-filter__option" @click="selectLocation(loc)">{{ loc }}</button>
          </li>
        </ul>
      </div>

      <!-- Department filter -->
      <div
        ref="departmentFilterRef"
        class="op-filter"
        :class="{ 'is-open': openDropdown === 'department' }"
      >
        <span class="op-filter__label">Department</span>
        <button type="button"
          class="op-filter__control"
          :class="{ 'is-placeholder': !filters.department }"
          :aria-expanded="openDropdown === 'department'"
          @click="toggleDropdown('department')">
          <span>{{ filters.department || 'All departments' }}</span>
          <span class="op-filter__chevron" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M5.83 8l4.17 4.17L14.17 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
        <ul v-show="openDropdown === 'department'" class="op-filter__list">
          <li>
            <button type="button" class="op-filter__option" @click="selectDepartment(null)">All departments</button>
          </li>
          <li v-for="dep in departments" :key="dep">
            <button type="button" class="op-filter__option" @click="selectDepartment(dep)">{{ dep }}</button>
          </li>
        </ul>
      </div>

    </div>

    <!-- The Job Listing -->
    <div class="open-positions__listing">
      <div v-for="group in groupedJobs" :key="group.department" class="open-positions__group">
        <h3 class="open-positions__group-title">{{ group.department }}</h3>
        <ul class="open-positions__jobs">
          <li v-for="job in group.jobs" :key="job.id">
            <a :href="jobUrl(job)" class="open-positions__job">
              <span class="open-positions__job-title">{{ job.title }}</span>
              <span class="open-positions__job-meta">{{ jobMeta(job) }}</span>
            </a>
          </li>
        </ul>
      </div>

      <p v-if="!filteredJobs.length" class="open-positions__empty">
        No open positions match your filters.
      </p>
    </div>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.open-positions__inner {
  max-width: 1160px;
  margin-inline: auto;
}

.open-positions__title {
  font-family: var(--font-family-heading);
  font-size: 28px;
  font-weight: 500;
  line-height: 124%;
  letter-spacing: -0.56px;
  color: #000;

  @media (min-width: 1024px) {
    font-size: 38px;
    line-height: 120%;
    letter-spacing: -0.76px;
  }
}

.open-positions__count {
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.36px;
  color: #A8A39B;
}

.open-positions__description {
  margin-top: 16px;
  font-family: var(--font-family-primary);
  font-size: 16px;
  line-height: 136%;
  letter-spacing: -0.32px;
  color: #736E68;
}

.open-positions__filters {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;

  @media (min-width: 1024px) {
    flex-direction: row;
    gap: 16px;
    margin-top: 32px;
  }
}

.op-filter {
  position: relative;
  width: 100%;

  @media (min-width: 1024px) {
    max-width: 376px;
  }
}

.op-filter__label {
  font-family: var(--font-family-primary);
  display: block;
  font-size: 13px;
  margin-bottom: 6px;
  line-height: 136%; 
  letter-spacing: -0.26px;
  color: var(--color-gray-350);
}

.op-filter__control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border: 1px solid #D9D6D2;
  border-radius: 8px;
  background-color: #fff;
  font-family: var(--font-family-primary);
  font-size: 14px;
  color: #66625E;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.op-filter__control:hover,
.op-filter.is-open .op-filter__control {
  border-color: #A8A39B;
}

.op-filter__control.is-placeholder {
  color: #66625E;
}

.op-filter__chevron {
  display: inline-flex;
  color: #66625E;
  transition: transform 0.2s ease;
}

.op-filter.is-open .op-filter__chevron {
  transform: rotate(180deg);
}

.op-filter__list {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 5;
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #E5E3DF;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 8px 24px rgba(41, 32, 22, 0.08);
}

.op-filter__option {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  text-align: left;
  font-family: var(--font-family-primary);
  font-size: 16px;
  color: #292016;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.op-filter__option:hover {
  background-color: #F7F5F0;
}

.open-positions__listing {
  margin-top: 32px;

  @media (min-width: 1024px) {
    margin-top: 60px;
  }
}

.open-positions__group + .open-positions__group {
  margin-top: 40px;

  @media (min-width: 1024px) {
    margin-top: 60px;
  }
}

.open-positions__group-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 500;
  line-height: 128%;
  letter-spacing: -0.4px;
  color: var(--color-black);
  margin-bottom: 12px;

  @media (min-width: 1024px) {
    font-size: 28px;
    line-height: 124%;
    margin-bottom: 24px;
    letter-spacing: -0.56px;
    color: var(--color-gray-600);
  }
}

.open-positions__job {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 0;
  border-bottom: 1px solid #D9D6D2;
  color: inherit;
  text-decoration: none;
  transition: background-color 0.3s ease;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px 0;
  }
}

.open-positions__job:hover {
  background-color: #f2f3f7;
}

.open-positions__job-title {
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 500;
  line-height: 136%;
  letter-spacing: -0.32px;
  color: #3B3530;
}

.open-positions__job-meta {
  font-family: var(--font-family-primary);
  font-size: 14px;
  line-height: 140%;
  letter-spacing: -0.28px;
  color: #736E68;

  @media (min-width: 1024px) {
    font-size: 16px;
    text-align: right;
    line-height: 136%;
    letter-spacing: -0.32px;
    flex-shrink: 0;
  }
}

.open-positions__empty {
  padding: 32px 0;
  font-family: var(--font-family-primary);
  font-size: 16px;
  color: #736E68;
}

</style>
